import { Components, Theme, alpha } from '@mui/material/styles';
import { radii, brand } from './tokens';
import { ITorqTheme } from './torqTypes';

// Mesmo limite de theme.breakpoints.down('sm') (< 600px).
const CELULAR = '@media (max-width:599.95px)';

// Chips de status (success/warning/error/info): fundo suave + texto escuro do
// mesmo tom, iguais em todos os módulos. Primary/default ficam de fora: chip
// primário sólido marca filtro selecionado (ver FilterBar).
const TONS_STATUS = {
  success: { main: brand.success, claro: brand.successButton, escuro: '#7BDD66' },
  warning: { main: brand.warning, claro: brand.warningButton, escuro: '#FFCF57' },
  error: { main: brand.error, claro: brand.errorButton, escuro: '#FF8A92' },
  info: { main: brand.info, claro: brand.infoButton, escuro: '#7DB2FF' },
};

// Construído identicamente por Light.ts e Dark.ts — antes cada arquivo
// sobrescrevia um subconjunto diferente de componentes MUI (Light tinha
// AppBar/Paper/Card/OutlinedInput/Button/Divider/LinearProgress, Dark tinha
// AppBar/Card/Button/Table/TableCell), o que fazia um tema ficar "mais
// completo" que o outro sem ninguém perceber. Uma função só elimina essa
// divergência (guia seção 60).
export const buildComponents = (mode: 'light' | 'dark', torq: ITorqTheme): Components<Theme> => ({
  // Barra de rolagem fina e discreta em TODA área rolável do sistema (não
  // só no body — as páginas rolam dentro dos próprios layouts), na cor do
  // tema. Trilho transparente, alça quase invisível que realça ao passar o
  // mouse sobre a área. Componentes que definem a própria barra (menu
  // lateral, tabelas) continuam valendo, por serem mais específicos.
  MuiCssBaseline: {
    styleOverrides: {
      '*': {
        scrollbarWidth: 'thin',
        scrollbarColor: `${torq.scrollbarThumb} transparent`,
      },
      '*:hover': {
        scrollbarColor: `${torq.scrollbarThumbHover} transparent`,
      },
      // Navegadores sem scrollbar-width/scrollbar-color (Safari e Chrome
      // antigos) usam os pseudo-elementos abaixo.
      '*::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
      },
      '*::-webkit-scrollbar-track, *::-webkit-scrollbar-corner': {
        background: 'transparent',
      },
      '*::-webkit-scrollbar-thumb': {
        backgroundColor: torq.scrollbarThumb,
        borderRadius: `${radii.pill}px`,
      },
      '*::-webkit-scrollbar-thumb:hover': {
        backgroundColor: torq.scrollbarThumbHover,
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: torq.surface.card,
        color: torq.text.primary,
        boxShadow: 'none',
        borderBottom: `1px solid ${torq.border.default}`,
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: radii.sm,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.8125rem',
        minHeight: 38,
      },
      outlined: {
        borderColor: torq.border.strong,
        '&:hover': { borderColor: torq.border.strong },
      },
      // Azul = ações secundárias; ciano puro não é legível como texto em
      // botão outlined/text sobre fundo claro, daí o tom escuro do matiz.
      outlinedPrimary: {
        color: torq.accentText,
        borderColor: alpha(brand.primaryDark, 0.4),
        '&:hover': { borderColor: torq.accentText, backgroundColor: torq.hover },
      },
      textPrimary: {
        color: torq.accentText,
        '&:hover': { backgroundColor: torq.hover },
      },
      containedPrimary: {
        '&:hover': { backgroundColor: brand.primaryHover },
      },
      containedSuccess: { '&:hover': { backgroundColor: '#3DB026' } },
      containedWarning: { '&:hover': { backgroundColor: '#EBA900' } },
      // Vermelho/azul com texto branco: o tom escuro é que dá contraste.
      containedError: { backgroundColor: brand.errorButton, '&:hover': { backgroundColor: '#B01C28' } },
      containedInfo: { backgroundColor: brand.infoButton, '&:hover': { backgroundColor: '#154FAB' } },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: { borderRadius: radii.sm },
      colorPrimary: { color: torq.accentText },
    },
  },

  MuiLink: {
    styleOverrides: { root: { color: mode === 'dark' ? '#7DB2FF' : brand.infoButton } },
  },

  MuiCheckbox: {
    styleOverrides: { root: { '&.Mui-checked': { color: torq.accentText } } },
  },

  MuiRadio: {
    styleOverrides: { root: { '&.Mui-checked': { color: torq.accentText } } },
  },

  MuiSwitch: {
    styleOverrides: {
      switchBase: {
        '&.Mui-checked': { color: torq.accentText },
        '&.Mui-checked + .MuiSwitch-track': { backgroundColor: torq.accentText },
      },
    },
  },

  MuiCircularProgress: {
    styleOverrides: { colorPrimary: { color: torq.accentText } },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: torq.surface.card,
      },
      rounded: { borderRadius: radii.lg },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: torq.surface.card,
        border: `1px solid ${torq.border.default}`,
        borderRadius: radii.lg,
        boxShadow: torq.cardShadow,
      },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 16,
        '&:last-child': { paddingBottom: 16 },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: radii.sm,
        backgroundColor: mode === 'dark' ? 'rgba(255,255,255,.03)' : '#FFFFFF',
        transition: 'border-color 150ms ease',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: mode === 'dark' ? torq.border.strong : '#D5E0E8',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: mode === 'dark' ? 'rgba(168,192,205,.38)' : '#B9CAD4',
        },
        // Foco: borda ciano + anel suave.
        '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(brand.primary, mode === 'dark' ? 0.22 : 0.3)}` },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: torq.border.focus,
          borderWidth: 1,
        },
      },
      input: {
        padding: '10px 14px',
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: torq.text.secondary,
        fontSize: '0.8125rem',
        '&.Mui-focused': { color: torq.accentText },
      },
    },
  },

  MuiSelect: {
    styleOverrides: {
      icon: { color: torq.text.secondary },
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        backgroundColor: torq.surface.elevated,
        border: `1px solid ${torq.border.default}`,
        borderRadius: radii.md,
        boxShadow: torq.dialogShadow,
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: ({ ownerState }) => {
        const tons = TONS_STATUS[ownerState.color as keyof typeof TONS_STATUS];
        if (!tons) return { borderRadius: radii.pill, fontWeight: 600 };
        const preenchido = ownerState.variant !== 'outlined';
        return {
          borderRadius: radii.pill,
          fontWeight: 600,
          color: mode === 'dark' ? tons.escuro : tons.claro,
          backgroundColor: preenchido ? alpha(tons.main, mode === 'dark' ? 0.2 : 0.15) : 'transparent',
          border: `1px solid ${alpha(tons.main, preenchido ? 0 : 0.55)}`,
          '& .MuiChip-icon': { color: 'inherit' },
          '&.MuiChip-clickable:hover': { backgroundColor: alpha(tons.main, 0.25) },
        };
      },
      outlined: {
        borderColor: torq.border.default,
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      root: { minHeight: 40 },
      indicator: { backgroundColor: brand.primary },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.8125rem',
        color: torq.text.secondary,
        minHeight: 40,
        '&.Mui-selected': { color: torq.accentText },
      },
    },
  },

  MuiTable: {
    styleOverrides: {
      root: { backgroundColor: 'transparent' },
    },
  },

  // Padrão de listagem do sistema (guia TORQ seção 5): cabeçalho com fundo
  // levemente destacado do corpo da tabela (`surface.page`, a mesma cor que
  // já quebra o branco da página), texto em versalete, e cor de texto
  // "sem cor" — #002233 (neutro escuro) no tema claro, texto claro neutro
  // no escuro, nunca a cor de acento.
  // No celular (< 600px, mesmo limite do breakpoint 'sm') as células ficam
  // mais compactas pra caber mais coluna na largura; o que não couber rola de
  // lado dentro do TableContainer (ver MuiTableContainer abaixo).
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${torq.border.default}`,
        fontSize: '0.8125rem',
        color: torq.text.primary,
        [CELULAR]: {
          padding: '6px 8px',
          fontSize: '0.75rem',
        },
      },
      head: {
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: torq.text.secondary,
        backgroundColor: torq.surface.level2,
        [CELULAR]: {
          fontSize: '0.65rem',
          letterSpacing: '0.02em',
          whiteSpace: 'nowrap',
        },
      },
    },
  },

  MuiTableContainer: {
    styleOverrides: {
      root: {
        [CELULAR]: {
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorX: 'contain',
        },
      },
    },
  },

  MuiTablePagination: {
    styleOverrides: {
      toolbar: {
        [CELULAR]: { flexWrap: 'wrap', paddingLeft: 8, paddingRight: 8, minHeight: 44 },
      },
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:hover': { backgroundColor: torq.hover },
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: { borderColor: torq.border.default },
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        backgroundColor: alpha(brand.primary, mode === 'dark' ? 0.18 : 0.25),
        borderRadius: 4,
      },
      bar: { borderRadius: 4, backgroundColor: mode === 'dark' ? brand.primary : '#12A6B0' },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: radii.xl,
        backgroundColor: torq.surface.elevated,
        border: `1px solid ${torq.border.default}`,
        boxShadow: torq.dialogShadow,
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: '1.125rem',
        fontWeight: 600,
        padding: '20px 24px',
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: { padding: '0 24px 20px' },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: { padding: '16px 24px', gap: 8 },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: radii.md,
        backgroundColor: torq.surface.elevated,
        border: `1px solid ${torq.border.default}`,
        boxShadow: torq.dialogShadow,
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        minHeight: 38,
        fontSize: '0.875rem',
        borderRadius: radii.xs,
        margin: '2px 6px',
        width: 'auto',
        '&.Mui-selected': { backgroundColor: torq.selected },
      },
    },
  },

  MuiPopover: {
    styleOverrides: {
      paper: {
        borderRadius: radii.md,
        backgroundColor: torq.surface.elevated,
        border: `1px solid ${torq.border.default}`,
        boxShadow: torq.dialogShadow,
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: brand.menuBase,
        color: '#F8FAFC',
        fontSize: '0.75rem',
        borderRadius: radii.xs,
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: radii.md },
    },
  },

  MuiPaginationItem: {
    styleOverrides: {
      root: {
        borderRadius: radii.sm,
        '&.Mui-selected': {
          backgroundColor: brand.primary,
          color: brand.menuBase,
          fontWeight: 700,
          '&:hover': { backgroundColor: brand.primaryHover },
        },
      },
    },
  },
});
